import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { GenerationProvider } from './contexts/GenerationContext';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth';
import { AuthenticatedLayout } from './components/layout';
import ErrorBoundary from './components/ErrorBoundary';
import { lazyWithRetry } from './utils/lazyWithRetry';
import HomePage from './pages/HomePage';
import ErrorPage from './pages/ErrorPage';
import LoginPage from './pages/LoginPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import ChangePasswordPage from './pages/ChangePasswordPage';

// Lazy-load heavy pages that are only needed after generation starts.
// This keeps the initial bundle small so the landing page loads fast.
// Using lazyWithRetry to handle chunk load failures after deployments.
const GeneratingPage = lazyWithRetry(() => import('./pages/GeneratingPage'));
const PreviewPage = lazyWithRetry(() => import('./pages/PreviewPage'));

// Lazy-load admin pages - only needed for admin users
const AdminDashboard = lazyWithRetry(() => import('./pages/admin/AdminDashboard'));
const CompaniesPage = lazyWithRetry(() => import('./pages/admin/CompaniesPage'));
const CompanyDetailPage = lazyWithRetry(() => import('./pages/admin/CompanyDetailPage'));
const UsersPage = lazyWithRetry(() => import('./pages/admin/UsersPage'));
const AllGenerationsPage = lazyWithRetry(() => import('./pages/admin/AllGenerationsPage'));
const BundlesPage = lazyWithRetry(() => import('./pages/admin/BundlesPage'));
const InvoicesPage = lazyWithRetry(() => import('./pages/admin/InvoicesPage'));
const PlatformSettingsPage = lazyWithRetry(() => import('./pages/admin/PlatformSettingsPage'));

// Lazy-load manager pages - only needed for manager users
const ManagerDashboard = lazyWithRetry(() => import('./pages/manager/ManagerDashboard'));
const CompanyHistoryPage = lazyWithRetry(() => import('./pages/manager/CompanyHistoryPage'));
const TransactionsPage = lazyWithRetry(() => import('./pages/manager/TransactionsPage'));
const DepositPage = lazyWithRetry(() => import('./pages/manager/DepositPage'));
const ManagerInvoicesPage = lazyWithRetry(() => import('./pages/manager/InvoicesPage'));
const ApiKeysPage = lazyWithRetry(() => import('./pages/manager/ApiKeysPage'));

// Lazy-load user pages - only needed for regular users
const UserDashboard = lazyWithRetry(() => import('./pages/user/UserDashboard'));
const MyGenerationsPage = lazyWithRetry(() => import('./pages/user/MyGenerationsPage'));

/** Full-screen centered loading fallback shown while a lazy chunk is fetched. */
function PageLoadingFallback() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 'var(--space-4)',
        backgroundColor: 'var(--color-bg)',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          border: '4px solid var(--color-border, #000)',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <p
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-sm)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          margin: 0,
        }}
      >
        Loading&hellip;
      </p>

      {/* Inline keyframes so the spinner works without an external stylesheet */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <GenerationProvider>
          <Suspense fallback={<PageLoadingFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              <Route path="/error" element={<ErrorPage />} />

              {/* All protected routes use AuthenticatedLayout for consistent header/sidebar */}

              {/* Change password route */}
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <ChangePasswordPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />

              {/* Generation flow routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <HomePage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/generating/:sessionId"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <GeneratingPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/generating"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <GeneratingPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/preview/:sessionId"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <PreviewPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />

              {/* User Dashboard routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <UserDashboard />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/generations"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <MyGenerationsPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />

              {/* Manager Dashboard routes */}
              <Route
                path="/manager"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMPANY_MANAGER']}>
                    <AuthenticatedLayout>
                      <ManagerDashboard />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/generations"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMPANY_MANAGER']}>
                    <AuthenticatedLayout>
                      <CompanyHistoryPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/transactions"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMPANY_MANAGER']}>
                    <AuthenticatedLayout>
                      <TransactionsPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/deposit"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMPANY_MANAGER']}>
                    <AuthenticatedLayout>
                      <DepositPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/invoices"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMPANY_MANAGER']}>
                    <AuthenticatedLayout>
                      <ManagerInvoicesPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/api-keys"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'COMPANY_MANAGER']}>
                    <AuthenticatedLayout>
                      <ApiKeysPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />

              {/* Admin routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                    <AuthenticatedLayout>
                      <AdminDashboard />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/companies"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                    <AuthenticatedLayout>
                      <CompaniesPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/companies/:slug"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                    <AuthenticatedLayout>
                      <CompanyDetailPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                    <AuthenticatedLayout>
                      <UsersPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/generations"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                    <AuthenticatedLayout>
                      <AllGenerationsPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/bundles"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                    <AuthenticatedLayout>
                      <BundlesPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/invoices"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN']}>
                    <AuthenticatedLayout>
                      <InvoicesPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                    <AuthenticatedLayout>
                      <PlatformSettingsPage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </GenerationProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
